import React, { useLayoutEffect, useRef, useState } from 'react';

import {
  Box,
  Collapse,
  Typography,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Grid,
} from '@mui/material';

import { styled } from '@mui/material';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { TraceHistory } from 'types';
import { getTracesAroundLogItem } from '../../api/snapshotAlarmLogApi';
import { useAppSelector } from '../../app/hooks';
import { useNavigate } from 'react-router';
import { SnapshotAlarmLogReturnWithStop } from './types';
import {
  combineMessageFields,
  findClosestTraceIndex,
  getLogIndexForManySnapshots,
  getLogIndexForOneSnapshot,
  getLogTime,
  removeDuplicateSnapshots,
} from './utils/logManipulation';
import InfoPopup from 'components/InfoPopup';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { useAllSnapshotsWithoutCrosspol } from '../snapshots/api/getAllSnapshotsWithoutCrosspol';
import { formatInTimeZone } from 'date-fns-tz';

interface Props {
  alarmLog: SnapshotAlarmLogReturnWithStop[];
}

const StyledTableCell = styled(TableCell)(
  ({ theme }) => `
  background-color: ${theme.palette.background.paper};
  `
);

const AlarmLogTable = ({ alarmLog }: Props) => {
  const { dateFormatWithZeros, timeFormat, timezone } = useUserDateTime();
  const useImperial = useAppSelector(
    (state) => state.auth.user?.us_customary_units
  );
  const [selectedLogItem, setSelectedLogItem] = useState('');
  const [noHistoryPopupOpen, setNoHistoryPopupOpen] = useState(false);
  const navigate = useNavigate();
  const { data: snapshots } = useAllSnapshotsWithoutCrosspol({});
  const [logArray, setLogArray] = useState<number[]>([]);
  const [numberOfLogsRendered, setNumberOfLogsRendered] = useState(25);
  const tableElement = useRef<HTMLDivElement | null>(null);

  const addIdArray = (alarmId: number) => {
    if (logArray.includes(alarmId)) {
      const filteredArray = logArray.filter((logItem) => logItem !== alarmId);
      setLogArray(filteredArray);
    } else {
      setLogArray([...logArray, alarmId]);
    }
  };

  const handleGoToTraceHistory = async (
    alarmLog: SnapshotAlarmLogReturnWithStop,
    alarmStop: boolean
  ) => {
    let traces: TraceHistory[];
    const logTime = getLogTime(alarmLog, alarmStop);
    if (
      removeDuplicateSnapshots(alarmLog.snapshot_alarm_log_items).length === 1
    ) {
      const logIndex = getLogIndexForOneSnapshot(alarmLog, alarmStop);
      traces = await getTracesAroundLogItem(
        alarmLog.snapshot_alarm_log_items[logIndex].id,
        50,
        50
      );
    } else {
      const logIndex = getLogIndexForManySnapshots(
        alarmLog,
        alarmStop,
        selectedLogItem
      );
      traces =
        logIndex === undefined
          ? []
          : await getTracesAroundLogItem(logIndex, 50, 50);
    }
    if (traces.length === 0) {
      setNoHistoryPopupOpen(true);
    } else {
      const fromDate = traces[0].time;
      const toDate = traces[traces.length - 1].time;
      if (fromDate > logTime || toDate < logTime) {
        setNoHistoryPopupOpen(true);
        return;
      }
      const centerIndex = findClosestTraceIndex(traces, logTime);
      navigate(
        `/trace-history?analyzer=${traces[0].analyzer_id}&snapshot=${traces[0].snapshot_id}&start=${fromDate}&end=${toDate}&trace=${centerIndex}`
      );
    }
  };

  const handleSnapshotSelect = (event: SelectChangeEvent) => {
    setSelectedLogItem(event.target.value);
  };
  const scrollListener = () => {
    if (!tableElement.current) return;
    let bottom =
      tableElement.current.scrollHeight - tableElement.current.clientHeight;
    if (
      tableElement.current.scrollTop + 200 >= bottom &&
      numberOfLogsRendered < alarmLog.length
    )
      setNumberOfLogsRendered((currentNumber) => currentNumber + 25);
  };

  useLayoutEffect(() => {
    const tableRef = tableElement.current;
    tableRef?.addEventListener('scroll', scrollListener);
    return () => {
      tableRef?.removeEventListener('scroll', scrollListener);
    };
  });

  return (
    <TableContainer
      component={Paper}
      ref={tableElement}
      sx={{
        maxHeight: '75vh',
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <StyledTableCell />
            <StyledTableCell>Alarm Name</StyledTableCell>
            <StyledTableCell>Datetime</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alarmLog?.slice(0, numberOfLogsRendered).map((item) => (
            <React.Fragment key={item.id}>
              <TableRow hover onClick={() => addIdArray(item.id)}>
                <TableCell>
                  <IconButton
                    aria-label='expand row'
                    size='small'
                    onClick={() => addIdArray(item.id)}
                  >
                    {logArray.includes(item.id) ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                </TableCell>
                <TableCell>
                  {item.alarm.name +
                    (item.snapshot_alarm_log_items?.some((logItem) =>
                      logItem.message.includes('Warning Mode: On')
                    )
                      ? ' (Warning)'
                      : '')}
                </TableCell>
                <TableCell>
                  {formatInTimeZone(
                    new Date(item.time),
                    timezone,
                    `${dateFormatWithZeros} ${timeFormat}`
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  style={{ paddingBottom: 0, paddingTop: 0 }}
                  colSpan={6}
                >
                  <Collapse
                    in={logArray.includes(item.id)}
                    timeout='auto'
                    unmountOnExit
                  >
                    <Box margin={1}>
                      <Typography variant='h6' gutterBottom component='div'>
                        Alarm Details
                      </Typography>
                      <TextField
                        variant='outlined'
                        sx={{
                          '& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled':
                            {
                              color: (theme) => theme.palette.text.primary,
                              '-webkit-text-fill-color': (theme) =>
                                theme.palette.text.primary,
                            },
                        }}
                        fullWidth
                        multiline
                        disabled
                        value={combineMessageFields(
                          item,
                          useImperial,
                          `${dateFormatWithZeros} ${timeFormat}`,
                          timezone
                        )}
                        onClick={() => addIdArray(item.id)}
                      ></TextField>
                      <Grid
                        style={{ marginTop: '0px' }}
                        container
                        alignItems='center'
                        justifyContent='flex-end'
                        spacing={1}
                      >
                        {removeDuplicateSnapshots(item.snapshot_alarm_log_items)
                          .length > 1 && (
                          <Grid item>
                            <FormControl sx={{ minWidth: 250 }}>
                              <InputLabel id='select-snapshot-label'>
                                Snapshot
                              </InputLabel>
                              <Select
                                labelId='select-snapshot-label'
                                value={selectedLogItem}
                                onChange={handleSnapshotSelect}
                                label='Snapshot'
                              >
                                {removeDuplicateSnapshots(
                                  item.snapshot_alarm_log_items
                                ).map((item) => (
                                  <MenuItem key={item.id} value={item.id}>
                                    {
                                      snapshots?.find(
                                        (snapshot) =>
                                          snapshot.id === item.snapshot_id
                                      )?.name
                                    }
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        <Grid item>
                          <Button
                            color='primary'
                            onClick={() => {
                              handleGoToTraceHistory(item, false).then();
                            }}
                            disabled={
                              removeDuplicateSnapshots(
                                item.snapshot_alarm_log_items
                              ).length > 1 &&
                              (!selectedLogItem ||
                                !item.snapshot_alarm_log_items
                                  .map((item) => item.id)
                                  .includes(Number(selectedLogItem)))
                            }
                          >
                            Go To {item.stopTime ? 'Start' : 'Trace'} History
                          </Button>
                        </Grid>
                        {item.stopTime && (
                          <Grid item>
                            <Button
                              color='primary'
                              onClick={() => {
                                handleGoToTraceHistory(item, true).then();
                              }}
                              disabled={
                                removeDuplicateSnapshots(
                                  item.snapshot_alarm_log_items
                                ).length > 1 &&
                                (!selectedLogItem ||
                                  !item.snapshot_alarm_log_items
                                    .map((item) => item.id)
                                    .includes(Number(selectedLogItem)))
                              }
                            >
                              Go To Stop History
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      <InfoPopup
        openCondition={noHistoryPopupOpen}
        title='No History Found'
        message='There is no recorded trace history for this alarm and the selected snapshot.'
        error
      />
    </TableContainer>
  );
};

export default AlarmLogTable;
